export const INTEGRATION_PROVIDERS = [
  "google",
  "proton",
  "whatsapp",
  "telegram",
  "slack",
  "youtube",
] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

export const INTEGRATION_SCOPES = [
  "calendar.read",
  "mail.read",
  "mail.send",
  "chat.read",
  "chat.send",
  "video.search",
  "video.read",
] as const;
export type IntegrationScope = (typeof INTEGRATION_SCOPES)[number];

export const INTEGRATION_ACTS = ["list", "read", "act"] as const;
export type IntegrationAct = (typeof INTEGRATION_ACTS)[number];

export const INTEGRATION_STATUSES = ["connected", "disconnected", "error"] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

export const PROVIDER_SCOPES: Record<IntegrationProvider, readonly IntegrationScope[]> = {
  google: ["calendar.read", "mail.read"],
  proton: ["mail.read"],
  whatsapp: ["chat.read", "chat.send"],
  telegram: ["chat.read", "chat.send"],
  slack: ["chat.read", "chat.send"],
  youtube: ["video.search", "video.read"],
};

export const PROVIDER_SECRET_FIELDS: Record<IntegrationProvider, readonly string[]> = {
  google: ["token"],
  proton: ["token", "userId"],
  whatsapp: ["token", "phoneNumberId"],
  telegram: ["token"],
  slack: ["token"],
  youtube: [],
};

export function needsNoSecret(provider: IntegrationProvider): boolean {
  return PROVIDER_SECRET_FIELDS[provider].length === 0;
}

export function scopesOfColumn(column: unknown): string[] {
  const said =
    typeof column === "string"
      ? column.replace(/^[{]/, "").replace(/[}]$/, "").split(",")
      : Array.isArray(column)
        ? column
        : [];
  return [
    ...new Set(
      said
        .map((one) => (typeof one === "string" ? one.trim().replace(/"/g, "") : ""))
        .filter((one) => one.length > 0),
    ),
  ];
}

export function isAScopeOf(provider: IntegrationProvider, scope: string): scope is IntegrationScope {
  return (PROVIDER_SCOPES[provider] as readonly string[]).includes(scope);
}

export function scopesOfAList(
  provider: IntegrationProvider,
  asked: readonly string[],
): IntegrationScope[] | null {
  const held = PROVIDER_SCOPES[provider];
  if (!held) return null;
  const kept: IntegrationScope[] = [];
  for (const scope of asked) {
    if (!held.includes(scope as IntegrationScope)) return null;
    if (!kept.includes(scope as IntegrationScope)) kept.push(scope as IntegrationScope);
  }
  return kept;
}

export function secretIsWellShaped(provider: IntegrationProvider, secret: unknown): boolean {
  if (secret === null || typeof secret !== "object" || Array.isArray(secret)) return false;
  const fields = PROVIDER_SECRET_FIELDS[provider];
  return Object.entries(secret as Record<string, unknown>).every(
    ([key, value]) => fields.includes(key) && typeof value === "string" && value.length > 0,
  );
}
